import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/api';
import { apiUrl } from '../../utils/apiUrl';
import { tokenManager, type Brand } from '../../utils/tokenManager';
import { useViewScope } from '../../utils/viewScope';
import {
  targetBrandFromRow,
  targetBrandHeader,
} from "../../shared/form/writeTargetBrand";
import {
  Typography,
  Table,
  Tag,
  Select,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Popconfirm,
  message,
  Upload,
} from "antd";
import type { UploadFile } from "antd";
import {
  EditOutlined,
  RollbackOutlined,
  DeleteOutlined,
  PlusOutlined,
  InboxOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Title } = Typography;
const { Dragger } = Upload;

type OtaPlatform = "android" | "ios" | "tv_android" | "tv_ios";
type OtaChannel = "production" | "staging" | "development";
type OtaUpdateType = "silent" | "prompt";

interface OtaRecord {
  id: number;
  /**
   * 去品牌化 M4：這筆 OTA row 的實際品牌（後端清單回傳 vpn_ota_update.vpn_brand）。
   * 編輯 / 回滾 / 刪除 = 既有 row 寫入，目標品牌 = row.brand，顯式帶 X-Target-Brand，
   * 絕不繼承檢視範圍 / activeBrand。
   */
  brand: Brand;
  platform: OtaPlatform;
  runtimeVersion: string;
  bundleId: string;
  bundleUrl: string;
  channel: OtaChannel;
  isActive: number | boolean;
  updateType: OtaUpdateType;
  message: string | null;
  bundleSize: number;
  createdAt: string;
}

interface OtaListResponse {
  items?: OtaRecord[];
  list?: OtaRecord[];
  total?: number;
}

const PLATFORM_LABELS: Record<string, string> = {
  android: "Android",
  ios: "iOS",
  tv_android: "Android TV",
  tv_ios: "Apple TV",
};

const PLATFORM_COLORS: Record<string, string> = {
  android: "green",
  ios: "blue",
  tv_android: "lime",
  tv_ios: "geekblue",
};

const CHANNEL_LABELS: Record<string, string> = {
  production: "正式",
  staging: "測試",
  development: "開發",
};

const CHANNEL_COLORS: Record<string, string> = {
  production: "green",
  staging: "orange",
  development: "blue",
};

const UPDATE_TYPE_LABELS: Record<string, string> = {
  silent: "靜默更新",
  prompt: "提示更新",
};

const PLATFORM_OPTIONS = [
  { label: "全部平台", value: "" },
  { label: "Android", value: "android" },
  { label: "iOS", value: "ios" },
  { label: "Android TV", value: "tv_android" },
  { label: "Apple TV", value: "tv_ios" },
];

const CHANNEL_OPTIONS = [
  { label: "全部頻道", value: "" },
  { label: "正式", value: "production" },
  { label: "測試", value: "staging" },
  { label: "開發", value: "development" },
];

const ACTIVE_OPTIONS = [
  { label: "全部狀態", value: "" },
  { label: "啟用", value: "1" },
  { label: "停用", value: "0" },
];

const UPDATE_TYPE_OPTIONS = [
  { label: "靜默更新", value: "silent" },
  { label: "提示更新", value: "prompt" },
];

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function toBooleanActive(value: number | boolean): boolean {
  return value === true || value === 1;
}

function normalizeResponse(result: OtaListResponse): OtaRecord[] {
  if (Array.isArray(result.items)) return result.items;
  if (Array.isArray(result.list)) return result.list;
  return [];
}

export const OtaList: React.FC = () => {
  // 檢視範圍（去品牌化 M1）：OTA 清單跟著 Header 檢視走；聚合檢視 brandForRequest=null。
  const { brandForRequest, isAllScope } = useViewScope();
  const [data, setData] = useState<OtaRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [platform, setPlatform] = useState("");
  const [channel, setChannel] = useState("");
  const [isActive, setIsActive] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [createLoading, setCreateLoading] = useState(false);
  const [bundleFiles, setBundleFiles] = useState<UploadFile[]>([]);
  const [assetFiles, setAssetFiles] = useState<UploadFile[]>([]);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [editLoading, setEditLoading] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  // M4：編輯既有 row 需要該 row 的實際品牌（editRecord.brand）當寫入目標品牌。
  const [editRecord, setEditRecord] = useState<OtaRecord | null>(null);

  const fetchData = useCallback(
    async (targetPage = page, targetPageSize = pageSize) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", String(targetPage));
        params.set("pageSize", String(targetPageSize));
        if (platform) params.set("platform", platform);
        if (channel) params.set("channel", channel);
        if (isActive) params.set("isActive", isActive);

        const res = await authFetch(
          `/api/admin/ota-updates?${params.toString()}`,
        );
        const result = await res.json();
        if (result.code === 0 && result.data) {
          const items = normalizeResponse(result.data as OtaListResponse);
          setData(items);
          setTotal(result.data.total ?? items.length);
        } else {
          message.error(result.msg || "獲取 OTA 列表失敗");
        }
      } catch {
        message.error("獲取 OTA 列表失敗");
      } finally {
        setLoading(false);
      }
      // brandForRequest 進 deps：檢視範圍變更時重拉 OTA 清單。
    },
    [page, pageSize, platform, channel, isActive, brandForRequest],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    // OTA 屬品牌隔離資源；聚合檢視下無明確目標品牌 → 先擋（M4 表單欄尚未接進 OTA）。
    if (isAllScope) {
      message.warning(
        "請先在右上角「檢視範圍」切換到單一品牌，再建立 OTA 更新。",
      );
      return;
    }
    createForm.setFieldsValue({
      platform: "android",
      channel: "production",
      updateType: "silent",
      runtimeVersion: "",
      message: "",
    });
    setBundleFiles([]);
    setAssetFiles([]);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    createForm.resetFields();
    setBundleFiles([]);
    setAssetFiles([]);
  };

  const openEdit = (record: OtaRecord) => {
    setEditId(record.id);
    setEditRecord(record);
    editForm.setFieldsValue({
      isActive: toBooleanActive(record.isActive),
      channel: record.channel,
      updateType: record.updateType,
      message: record.message ?? "",
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    editForm.resetFields();
    setEditId(null);
    setEditRecord(null);
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      if (bundleFiles.length === 0 || !bundleFiles[0].originFileObj) {
        message.error("請先選擇 bundle 檔案");
        return;
      }

      // M4：OTA 屬品牌隔離寫入，必須顯式目標品牌。聚合檢視已於 openCreate 擋掉。
      if (!brandForRequest) {
        message.error("請先切換到單一品牌檢視再建立 OTA。");
        return;
      }

      setCreateLoading(true);
      const formData = new FormData();
      formData.append("platform", values.platform);
      formData.append("runtimeVersion", values.runtimeVersion);
      formData.append("channel", values.channel);
      formData.append("updateType", values.updateType);
      if (values.message) {
        formData.append("message", values.message);
      }
      // multipart 一併帶 targetBrand form field（body 通道），header 為主。
      formData.append("targetBrand", brandForRequest);
      formData.append("bundle", bundleFiles[0].originFileObj as File);
      for (const file of assetFiles) {
        if (file.originFileObj) {
          formData.append("assets", file.originFileObj as File);
        }
      }

      const token = await tokenManager.getToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      // M4：multipart 不便靠 body 解析品牌 → X-Target-Brand header 為準。
      headers["X-Target-Brand"] = brandForRequest;

      // VPN-101：apiUrl 供跨域部署。刪 credentials:'include' — 後端 CORS 未設
      // Allow-Credentials，跨域下 include 會被瀏覽器 reject；認證走 Bearer header
      // 不靠 cookie，同源下刪除中性。
      const res = await fetch(apiUrl('/api/admin/ota-updates'), {
        method: 'POST',
        headers,
        body: formData,
      });
      const result = await res.json();

      if (result.code === 0) {
        message.success("OTA Bundle 上傳成功");
        closeCreate();
        setPage(1);
        fetchData(1, pageSize);
      } else {
        message.error(result.msg || "上傳失敗");
      }
    } catch {
      message.error("請確認表單欄位與上傳檔案");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEdit = async () => {
    if (editId === null || !editRecord) {
      message.error("找不到要更新的 OTA 記錄");
      return;
    }
    // M4：OTA 更新為既有 row 寫入。清單回傳每筆 row 的 brand 欄 → 目標品牌 =
    // row.brand（editRecord.brand），顯式帶 X-Target-Brand + body.targetBrand，絕不
    // 繼承檢視範圍 / activeBrand。對齊 backend resolveExistingRowWriteTargetBrand。
    const tEdit = targetBrandFromRow(editRecord.brand);
    if (!tEdit.ok) {
      message.warning(tEdit.reason);
      return;
    }

    try {
      const values = await editForm.validateFields();
      setEditLoading(true);
      const res = await authFetch(`/api/admin/ota-updates/${editId}`, {
        method: "PUT",
        headers: targetBrandHeader(tEdit.brand),
        body: JSON.stringify({ ...values, targetBrand: tEdit.brand }),
      });
      const result = await res.json();
      if (result.code === 0) {
        message.success("OTA 更新已修改");
        closeEdit();
        fetchData();
      } else {
        message.error(result.msg || "更新失敗");
      }
    } catch {
      message.error("請確認編輯表單內容");
    } finally {
      setEditLoading(false);
    }
  };

  const handleRollback = async (record: OtaRecord) => {
    // M4：回滾為既有 row 寫入，目標品牌 = row.brand（顯式 X-Target-Brand），絕不繼承
    // 檢視範圍 / activeBrand。對齊 backend resolveExistingRowWriteTargetBrand。
    const t = targetBrandFromRow(record.brand);
    if (!t.ok) {
      message.warning(t.reason);
      return;
    }
    try {
      const res = await authFetch(
        `/api/admin/ota-updates/${record.id}/rollback`,
        {
          method: "POST",
          headers: targetBrandHeader(t.brand),
        },
      );
      const result = await res.json();
      if (result.code === 0) {
        message.success("已回滾至前一版本");
        fetchData();
      } else {
        message.error(result.msg || "回滾失敗");
      }
    } catch {
      message.error("回滾請求失敗");
    }
  };

  const handleDelete = async (record: OtaRecord) => {
    // M4：刪除為既有 row 寫入，目標品牌 = row.brand（顯式 X-Target-Brand），絕不繼承
    // 檢視範圍 / activeBrand。對齊 backend resolveExistingRowWriteTargetBrand。
    const t = targetBrandFromRow(record.brand);
    if (!t.ok) {
      message.warning(t.reason);
      return;
    }
    try {
      const res = await authFetch(`/api/admin/ota-updates/${record.id}`, {
        method: "DELETE",
        headers: targetBrandHeader(t.brand),
      });
      const result = await res.json();
      if (result.code === 0) {
        message.success("已刪除");
        fetchData();
      } else {
        message.error(result.msg || "刪除失敗");
      }
    } catch {
      message.error("刪除請求失敗");
    }
  };

  return (
    <div className="page-enter">
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
        align="center"
      >
        <Title level={4} style={{ margin: 0 }} data-testid="ota-title">
          OTA 更新管理
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchData()}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            上傳 Bundle
          </Button>
        </Space>
      </Space>

      <Space wrap className="mobile-filters" style={{ marginBottom: 12 }}>
        <Select
          style={{ width: 160 }}
          value={platform}
          onChange={(value) => {
            setPage(1);
            setPlatform(value);
          }}
          options={PLATFORM_OPTIONS}
        />
        <Select
          style={{ width: 140 }}
          value={channel}
          onChange={(value) => {
            setPage(1);
            setChannel(value);
          }}
          options={CHANNEL_OPTIONS}
        />
        <Select
          style={{ width: 140 }}
          value={isActive}
          onChange={(value) => {
            setPage(1);
            setIsActive(value);
          }}
          options={ACTIVE_OPTIONS}
        />
      </Space>

      <Table
        dataSource={data}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1220 }}
        data-testid="ota-table"
        locale={{
          emptyText: loading ? undefined : (
            <div style={{ padding: "40px 0" }}>
              <p>尚無 OTA 更新紀錄</p>
            </div>
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 筆`,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
      >
        <Table.Column dataIndex="id" title="ID" width={70} sorter />
        <Table.Column
          dataIndex="platform"
          title="平台"
          width={120}
          render={(value: string) => (
            <Tag color={PLATFORM_COLORS[value] || "default"}>
              {PLATFORM_LABELS[value] || value}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="runtimeVersion"
          title="Runtime 版本"
          width={120}
        />
        <Table.Column
          dataIndex="bundleId"
          title="Bundle ID"
          width={180}
          ellipsis
        />
        <Table.Column
          dataIndex="bundleUrl"
          title="Bundle URL"
          width={210}
          ellipsis
          render={(value: string) => <span title={value}>{value || "-"}</span>}
        />
        <Table.Column
          dataIndex="channel"
          title="頻道"
          width={110}
          render={(value: string) => (
            <Tag color={CHANNEL_COLORS[value] || "default"}>
              {CHANNEL_LABELS[value] || value}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="updateType"
          title="更新類型"
          width={110}
          render={(value: string) => UPDATE_TYPE_LABELS[value] || value}
        />
        <Table.Column
          dataIndex="bundleSize"
          title="檔案大小"
          width={110}
          render={(value: number) => formatFileSize(value)}
        />
        <Table.Column
          dataIndex="isActive"
          title="狀態"
          width={90}
          render={(value: number | boolean) => (
            <Tag color={toBooleanActive(value) ? "green" : "default"}>
              {toBooleanActive(value) ? "啟用" : "停用"}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="createdAt"
          title="時間"
          width={170}
          sorter
          render={(value: string) =>
            value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "-"
          }
        />
        <Table.Column
          title="操作"
          width={220}
          fixed="right"
          render={(_: unknown, record: OtaRecord) => (
            <Space>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(record)}
                data-testid="ota-edit-btn"
                aria-label="編輯 OTA 更新"
                // M4：聚合檢視無法判定目標品牌 → 禁用，引導切單一品牌檢視。
                disabled={isAllScope}
                title={isAllScope ? "請先切換到單一品牌檢視再編輯" : undefined}
              >
                編輯
              </Button>
              <Popconfirm
                title="確定回滾至前一版本？"
                onConfirm={() => handleRollback(record)}
                okText="確定"
                cancelText="取消"
                disabled={isAllScope}
              >
                <Button
                  size="small"
                  icon={<RollbackOutlined />}
                  data-testid="ota-rollback-btn"
                  aria-label="回滾 OTA 更新"
                  disabled={isAllScope}
                  title={
                    isAllScope ? "請先切換到單一品牌檢視再回滾" : undefined
                  }
                >
                  回滾
                </Button>
              </Popconfirm>
              <Popconfirm
                title="確定刪除此 OTA 更新？"
                onConfirm={() => handleDelete(record)}
                okText="確定"
                cancelText="取消"
                disabled={isAllScope}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  data-testid="ota-delete-btn"
                  aria-label="刪除 OTA 更新"
                  disabled={isAllScope}
                  title={
                    isAllScope ? "請先切換到單一品牌檢視再刪除" : undefined
                  }
                >
                  刪除
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>

      <Modal
        title="上傳 OTA Bundle"
        open={createOpen}
        onOk={handleCreate}
        onCancel={closeCreate}
        confirmLoading={createLoading}
        okText="上傳"
        cancelText="取消"
        width={760}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{
            platform: "android",
            channel: "production",
            updateType: "silent",
          }}
        >
          <Form.Item
            name="platform"
            label="平台"
            rules={[{ required: true, message: "請選擇平台" }]}
          >
            <Select options={PLATFORM_OPTIONS.slice(1)} />
          </Form.Item>
          <Form.Item
            name="runtimeVersion"
            label="Runtime 版本"
            rules={[{ required: true, message: "請輸入 Runtime 版本" }]}
          >
            <Input placeholder="例如 1.0.0" />
          </Form.Item>
          <Form.Item
            name="channel"
            label="頻道"
            rules={[{ required: true, message: "請選擇頻道" }]}
          >
            <Select options={CHANNEL_OPTIONS.slice(1)} />
          </Form.Item>
          <Form.Item
            name="updateType"
            label="更新類型"
            rules={[{ required: true, message: "請選擇更新類型" }]}
          >
            <Select options={UPDATE_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="message" label="更新訊息">
            <Input.TextArea
              rows={3}
              placeholder="選填，顯示給客戶端的更新說明"
            />
          </Form.Item>

          <Form.Item
            label="Bundle 檔案"
            required
            tooltip="檔案會以 bundle.js 上傳到 backend"
          >
            <Dragger
              accept=".js"
              maxCount={1}
              fileList={bundleFiles}
              beforeUpload={() => false}
              onChange={({ fileList }) => setBundleFiles(fileList.slice(-1))}
              onRemove={() => {
                setBundleFiles([]);
                return true;
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">點擊或拖曳 bundle.js 到這裡</p>
              <p className="ant-upload-hint">
                上傳前請確認檔名與版本對應正確。
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item label="Assets" tooltip="選填，可同時上傳多個資產檔案">
            <Dragger
              multiple
              accept=".js,.json,.png,.jpg,.jpeg,.gif,.svg,.ttf,.otf,.woff,.woff2"
              fileList={assetFiles}
              beforeUpload={() => false}
              onChange={({ fileList }) => setAssetFiles(fileList)}
              onRemove={(file) => {
                setAssetFiles((current) =>
                  current.filter((item) => item.uid !== file.uid),
                );
                return true;
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">可選擇多個 assets 檔案</p>
              <p className="ant-upload-hint">
                後端會以 `assets` 欄位接收多檔。
              </p>
            </Dragger>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="編輯 OTA 更新"
        open={editOpen}
        onOk={handleEdit}
        onCancel={closeEdit}
        confirmLoading={editLoading}
        okText="確定"
        cancelText="取消"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="isActive" label="啟用狀態" valuePropName="checked">
            <Switch checkedChildren="啟用" unCheckedChildren="停用" />
          </Form.Item>
          <Form.Item
            name="channel"
            label="頻道"
            rules={[{ required: true, message: "請選擇頻道" }]}
          >
            <Select options={CHANNEL_OPTIONS.slice(1)} />
          </Form.Item>
          <Form.Item
            name="updateType"
            label="更新類型"
            rules={[{ required: true, message: "請選擇更新類型" }]}
          >
            <Select options={UPDATE_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="message" label="更新訊息">
            <Input.TextArea rows={3} placeholder="此版本的更新說明..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
